import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SEO } from "@/components/shared/SEO";

export default function ResetPassword() {
  const { token } = useParams<{ token: string }>();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== pw2) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pw });
      localStorage.setItem("sg_token", data.token);
      toast.success("Password reset — you're logged in.");
      nav("/dashboard");
      setTimeout(() => window.location.reload(), 300);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[color:var(--sg-bg)]">
      <SEO title="Reset password" path="/reset-password" />
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="h-10 w-10 rounded-lg bg-[color:var(--sg-fg)] flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div className="font-display text-2xl font-bold tracking-tight">SociBaba</div>
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Choose a new password</h1>
        <p className="text-sm text-[color:var(--sg-muted)] mb-6">
          This link can only be used once. Make it strong — at least 6 characters.
        </p>
        <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">New password</Label>
            <Input data-testid="reset-pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)}
                    minLength={6} required className="h-11 bg-white" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">Confirm password</Label>
            <Input data-testid="reset-pw2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)}
                    minLength={6} required className="h-11 bg-white" />
          </div>
          <Button type="submit" data-testid="reset-submit" disabled={busy}
                  className="w-full h-11 bg-[color:var(--sg-fg)] text-white hover:bg-black font-semibold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset password"}
          </Button>
        </form>
        <p className="text-sm text-[color:var(--sg-muted)] mt-6 text-center">
          <Link to="/login" className="text-[color:var(--sg-primary)] font-semibold hover:underline">Back to login</Link>
        </p>
      </div>
    </div>
  );
}
